import { CloudOff } from 'lucide-react';
import { useCallback, useEffect, useState } from 'react';
import { api, type HealthPayload } from '../lib/api';
import {
  currentSession,
  connectDrive,
  onSessionChange,
  renderSignIn,
  watchSession,
  type Session,
} from '../lib/auth';
import { resolveTheme, watchSystemTheme } from '../lib/theme';
import { useAppStore } from '../store/useAppStore';

/**
 * The gate in front of the app when it is hosted.
 *
 * Renders its children unchanged when sign-in is switched off, which is the
 * whole local story: the app is single-user on a laptop and there is nothing to
 * sign in to. Hosted, the server says `authRequired` on `/api/health` — the one
 * route that answers without a credential — so the SPA knows to ask rather than
 * finding out from the first 401 it causes.
 *
 * Two steps, and they are separate for a reason that is not cosmetic. Google's
 * button gives us an identity. Reaching Drive needs a second grant, and that
 * grant opens a popup — which browsers only allow from a real user gesture.
 * Chaining it onto the end of the sign-in callback is not a gesture, so the popup
 * is blocked, and a blocked popup never calls back: no prompt, no error, a
 * promise that never settles.
 */
export function SignIn({ children }: { children: React.ReactNode }) {
  const [health, setHealth] = useState<HealthPayload | null>(null);
  const [session, setSession] = useState<Session | null>(currentSession);
  const [error, setError] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);
  /** Google would not renew the Drive grant on its own; only a click can now. */
  const [driveLost, setDriveLost] = useState(false);
  const theme = useAppStore((s) => s.theme);
  const dark = resolveTheme(theme) === 'dark';

  useEffect(() => onSessionChange(setSession), []);

  /*
   * The gate paints the theme itself. App does the same once it mounts, but App
   * does not mount until this is satisfied — so without this the sign-in screen
   * renders in whatever state the document happened to be left in, and Google's
   * button is drawn to match the wrong one.
   */
  useEffect(() => {
    const paint = () => {
      document.documentElement.classList.toggle('dark', resolveTheme(theme) === 'dark');
    };
    paint();
    return theme === 'system' ? watchSystemTheme(paint) : undefined;
  }, [theme]);

  useEffect(() => {
    api
      .health()
      .then(setHealth)
      .catch((cause: unknown) => setError(message(cause)));
  }, []);

  const clientId = health?.clientId ?? '';

  /*
   * The clock and the person, watched for as long as somebody is signed in.
   *
   * Mounted here rather than in `App` because this component outlives it: when
   * the session ends, App unmounts and the watch has to be the thing that says
   * so rather than a casualty of it.
   */
  const signedIn = session !== null;
  useEffect(() => {
    if (!signedIn || !clientId) return;
    return watchSession({
      clientId,
      onEnded: (reason) =>
        setError(
          reason === 'idle'
            ? 'Signed out after half an hour away. Everything you had open is saved.'
            : 'Your sign-in has run out for today. Everything you had open is saved.',
        ),
      onDriveLost: () => setDriveLost(true),
    });
  }, [signedIn, clientId]);

  /** Google draws its own button here; ours would have to use One Tap, which browsers suppress. */
  const mount = useCallback(
    (element: HTMLDivElement | null) => {
      if (!element || !clientId) return;
      renderSignIn(clientId, element, { dark }).catch((cause: unknown) => setError(message(cause)));
    },
    [clientId, dark],
  );

  /*
   * Off a click, which is the entire point and now the only way it happens.
   *
   * Google's token client opens a window whatever `prompt` says — `prompt: ''`
   * stops it *showing* anything, not opening — and a window this app did not
   * open from a click is one the browser blocks, silently, so nothing ever
   * calls back. There used to be a request on mount as well, and another on a
   * timer; between them they made Drive look permanently flaky. The app now
   * waits to be asked.
   */
  const askForDrive = () => {
    setBusy(true);
    setError(null);
    setDriveLost(false);
    connectDrive(clientId)
      .catch((cause: unknown) => setError(message(cause)))
      .finally(() => setBusy(false));
  };

  // Nothing is known yet. Deliberately blank rather than a spinner: this is one
  // request to a route that does no work, and a flash of "loading" is worse.
  if (!health) return error ? <Problem message={error} /> : null;
  if (!health.authRequired) return <>{children}</>;

  /*
   * Drive access is required, not optional. Without it the server has no token
   * to write with and scenarios land on the container's own disk, which Cloud Run
   * throws away — so letting somebody in without it would lose their work
   * quietly, which is the worst way to lose it.
   */
  /*
   * The app stays up when the Drive grant goes stale rather than being replaced
   * by the consent screen. Unmounting it would throw away the plan on screen to
   * ask for something a corner notice can ask for.
   */
  if (session?.driveToken) {
    return (
      <>
        {children}
        {driveLost ? <ReconnectDrive busy={busy} onReconnect={askForDrive} /> : null}
      </>
    );
  }

  return (
    <main className="flex h-full w-full items-center justify-center bg-[var(--color-surface-2)] p-6">
      <div className="w-full max-w-sm rounded-2xl border border-[var(--color-line)] bg-[var(--color-surface)] p-8 text-center shadow-[var(--shadow-panel)]">
        {/* The one screen with room for it, and the first thing anybody sees. */}
        <img
          src="/tive-mark.svg"
          alt=""
          aria-hidden
          width={56}
          height={56}
          className="mx-auto mb-4 size-14"
        />
        <h1 className="font-[var(--font-wide)] text-[19px] font-semibold text-[var(--color-ink)]">
          Tive Org Planning
        </h1>

        {session ? (
          <>
            <p className="mt-2 text-[13px] text-[var(--color-ink-3)]">
              Signed in as <span className="text-[var(--color-ink-2)]">{session.name}</span>. One more step:
              your scenarios are saved to your own Drive, and directory photos come from Workspace.
            </p>
            <button
              type="button"
              onClick={askForDrive}
              disabled={busy}
              className="mt-6 w-full rounded-xl bg-[var(--color-brand-fill)] px-4 py-2.5 text-[13.5px] font-medium text-white transition hover:brightness-110 disabled:opacity-60"
            >
              {busy ? 'Waiting for Google…' : 'Continue'}
            </button>
            <p className="mt-3 text-[11.5px] text-[var(--color-ink-3)]">
              Google will open a window asking for Drive and directory access.
            </p>
          </>
        ) : (
          <>
            <p className="mt-2 text-[13px] text-[var(--color-ink-3)]">
              Sign in with your Tive Google account. Your scenarios are saved to your own Drive.
            </p>
            {/*
              Clipped to the button's own shape. Google renders the personalized
              "Sign in as …" variant inside an iframe with its own white
              backdrop, which `theme` does not reach — so on a dark card it
              reads as a white slab with a button floating in it. Rounding and
              hiding the overflow trims that backdrop to the pill, whichever
              variant Google decides to draw.
            */}
            <div className="mt-6 flex justify-center">
              {/*
                The clip shrink-wraps the button rather than the row. A full
                width box rounded to a pill curves in the wrong places -- the
                button is narrower and centred, so its own corners sit inside
                the curve and stay square.
              */}
              <div className="w-fit overflow-hidden rounded-full" ref={mount} data-testid="google-button" />
            </div>
          </>
        )}

        {error ? <p className="mt-4 text-[12.5px] text-[var(--color-moved)]">{error}</p> : null}
      </div>
    </main>
  );
}

function message(cause: unknown): string {
  return cause instanceof Error ? cause.message : String(cause);
}

function Problem({ message: text }: { message: string }) {
  return (
    <main className="flex h-full w-full items-center justify-center p-6">
      <p className="max-w-sm text-center text-[13px] text-[var(--color-moved)]">{text}</p>
    </main>
  );
}

/**
 * Drive would not renew itself, so a person has to ask.
 *
 * Access tokens last about an hour and the silent renewal only works while the
 * grant stands; a revoked one, or a Google account signed out in another tab,
 * needs a click. It has to *be* a click, too — a popup opened from a timer is
 * blocked without saying so.
 */
function ReconnectDrive({ busy, onReconnect }: { busy: boolean; onReconnect: () => void }) {
  return (
    <div
      role="status"
      className="fixed bottom-4 left-4 z-40 flex max-w-[19rem] items-start gap-2.5 rounded-xl border border-[var(--color-leaving)] bg-[var(--color-surface)] p-3 shadow-[var(--shadow-panel)]"
    >
      <CloudOff size={15} className="mt-0.5 shrink-0 text-[var(--color-leaving)]" aria-hidden />
      <div className="min-w-0">
        <p className="text-[12.5px] font-semibold text-[var(--color-ink)]">Drive needs reconnecting</p>
        <p className="mt-0.5 text-[11.5px] leading-snug text-[var(--color-ink-3)]">
          Changes cannot be saved until it is. Nothing you have done is lost.
        </p>
        <button
          type="button"
          onClick={onReconnect}
          disabled={busy}
          className="mt-2 rounded-lg bg-[var(--color-brand-fill)] px-2.5 py-1 text-[12px] font-semibold text-white transition-opacity hover:opacity-90 disabled:opacity-60"
        >
          {busy ? 'Waiting for Google…' : 'Reconnect'}
        </button>
      </div>
    </div>
  );
}
